import Css from "./style.module.scss";

import * as Icons from "@phosphor-icons/react";
import { Button } from "nlib/ui";
import { checkCommentsFetching } from "selectors/comments";
import { checkIsBusinessUser } from "selectors/user";
import { checkSelectedBusinessHasBusinessUsers, getSelectedBusinessUsersData } from "selectors/businesses";
import { getActiveOrganization } from "selectors/organizations";
import { getTextsData } from "selectors/texts";
import { useDispatch, useSelector } from "react-redux";
import AutoCompleteInput from "nlib/ui/AutoCompleteInput";
import CommentTypes from "const/CommentTypes";
import CommentsActions from "actions/CommentsActions";
import Constants from "const/Constants";
import KeyNames from "const/KeyNames";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Texts from "utils/Texts";
import classNames from "classnames";

const { MENTION_TRIGGER_CHAR } = Constants;

const TRIGGER_CHAR_PATTERN = new RegExp(`${MENTION_TRIGGER_CHAR}(\\S*)$`);

const AddCommentField = ({ onlyNotes, ...props }) => {
  const defaultCommentType = onlyNotes ? CommentTypes.NOTE : CommentTypes.COMMENT;

  const messageDraftRef = useRef({ [CommentTypes.COMMENT]: "", [CommentTypes.NOTE]: "" });

  const [commentType, setCommentType] = useState(defaultCommentType);

  const [innerValue, setComment] = useState(messageDraftRef.current[defaultCommentType]);

  const innerRef = useRef();

  const {
    id,
    type,
    portal,
    popupMode,
    commentsLoaded,
    compact,
    disabled,
    className,
    placeholder,
    autoHeight = true,
    alignUserListRight,
    inputRef = innerRef,
    value: comment = innerValue,
    onChange = setComment,
    onFocus,
    onBlur,
    onAskClient,
    ...restProps
  } = props;

  const dispatch = useDispatch();

  const { uiTexts, messages } = useSelector(getTextsData);

  const usersData = useSelector(getSelectedBusinessUsersData);

  const businessUser = useSelector(checkIsBusinessUser);

  const fetchingComments = useSelector(checkCommentsFetching);

  const selectedBusinessHasBusinessUsers = useSelector(checkSelectedBusinessHasBusinessUsers);

  const { businessOrganization } = useSelector(getActiveOrganization);

  const [opened, setOpened] = useState(false);

  const [focused, setFocused] = useState(false);

  const [commentSent, setCommentSent] = useState(false);

  const askClientAvailable = !businessOrganization || selectedBusinessHasBusinessUsers;

  const outerButtons = onAskClient && !businessUser && askClientAvailable;

  const usersEmails = useMemo(() => {
    return usersData.map(({ email, fullName }) => email + (fullName === email ? "" : ` (${fullName})`));
  }, [usersData]);

  const filteredUsersEmails = useMemo(() => {
    const triggerMatch = comment.match(TRIGGER_CHAR_PATTERN);

    if (!triggerMatch) return [];

    const [, searchString] = triggerMatch;

    if (!searchString) return usersEmails;

    return usersEmails.filter((item) => {
      const itemValue = item.trim().toLowerCase();

      return itemValue === searchString || itemValue.includes(searchString);
    });
  }, [comment, usersEmails]);

  const sendComment = useCallback(() => {
    const message = comment.trim();

    if (!message) return;

    const mentioned = [...new Set(message.match(new RegExp(`${MENTION_TRIGGER_CHAR}\\S*`, "g")) || [])]
      .map((mention) => {
        return (usersData.find(({ email }) => email === mention.slice(1)) || {}).sub;
      })
      .filter(Boolean);

    messageDraftRef.current[commentType] = "";
    dispatch(CommentsActions.addNewComment(commentType, message, mentioned, null, id, type));
    onChange("");
    setFocused(false);
    setCommentSent(true);
  }, [comment, commentType, dispatch, id, type, onChange, usersData]);

  const handleSendMessageClick = useCallback(() => {
    sendComment();
  }, [sendComment]);

  const handleAutoCompleteClick = useCallback((event) => {
    event.stopPropagation();
  }, []);

  const handleCommentTextChange = useCallback((text) => {
    if (text.match(TRIGGER_CHAR_PATTERN)) {
      setOpened(true);
    }
    messageDraftRef.current[commentType] = text;
    onChange(text);
  }, [commentType, onChange]);

  const handleAutoComplete = useCallback((text) => {
    onChange((prev) => {
      const value = prev && `${prev.slice(0, prev.lastIndexOf(MENTION_TRIGGER_CHAR) + 1) + text} `;

      messageDraftRef.current[commentType] = value;

      return value;
    });
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [commentType, inputRef, onChange]);

  const handleTypeSelectorClick = useCallback((event) => {
    const { type: nextType } = event.target.dataset;

    messageDraftRef.current[commentType] = comment;
    setCommentType(nextType);
    onChange(messageDraftRef.current[nextType]);
  }, [comment, commentType, onChange]);

  const handleAskClient = useCallback(() => {
    sendComment();
    onAskClient();
  }, [sendComment, onAskClient]);

  const handleInputFocus = useCallback((event) => {
    if (onFocus) onFocus(event);
    setFocused(true);
  }, [onFocus]);

  const handleInputBlur = useCallback((event) => {
    if (onBlur) onBlur(event);
    setFocused(false);
  }, [onBlur]);

  const handleKeyDown = useCallback((event) => {
    if (event.key !== KeyNames.ENTER || event.shiftKey || event.ctrlKey) return;
    event.preventDefault();
    sendComment();
  }, [sendComment]);

  useEffect(() => {
    if ((popupMode && commentsLoaded) || commentSent) {
      if (!disabled && !fetchingComments && inputRef.current && document.activeElement !== inputRef.current) {
        inputRef.current.focus();
      }
    }
  }, [popupMode, commentsLoaded, commentSent, disabled, fetchingComments, inputRef]);

  return (
    <div
      className={classNames(
        Css.addCommentField,
        outerButtons && Css.outerButtons,
        commentType === CommentTypes.NOTE && Css.typeNote,
        className
      )}>
      <div className={Css.container}>
        {!businessUser && !compact && (
          <div className={classNames(Css.typeSelector, focused && Css.focused)} onClick={handleTypeSelectorClick}>
            {!onlyNotes && (
              <div
                className={classNames(Css.type, commentType === CommentTypes.COMMENT && Css.active)}
                data-type={CommentTypes.COMMENT}>{uiTexts.reply}</div>
            )}
            <div
              className={classNames(Css.type, commentType === CommentTypes.NOTE && Css.active)}
              data-type={CommentTypes.NOTE}>{uiTexts.note}</div>
          </div>
        )}
        <div className={Css.relative}>
          <AutoCompleteInput
            {...restProps}
            autoHeight={autoHeight}
            selectOnFocus={false}
            alignListRight={alignUserListRight}
            portal={portal}
            multiline={!compact}
            opened={opened}
            disabled={disabled || fetchingComments}
            useCaret={false}
            filteredItems={filteredUsersEmails}
            inputRef={inputRef}
            className={classNames(Css.autoComplete, !businessUser && !compact && Css.withTabs)}
            placeholder={placeholder || (compact
              ? uiTexts.enterComment
              : Texts.replaceVars(
                businessUser
                  ? messages.commentPlaceholder
                  : (commentType === CommentTypes.COMMENT ? messages.commentReplyPlaceholder : messages.commentNotePlaceholder),
                { triggerChar: MENTION_TRIGGER_CHAR }
              ))}
            value={comment}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onKeyDown={handleKeyDown}
            onClick={handleAutoCompleteClick}
            onChange={handleCommentTextChange}
            onAutoComplete={handleAutoComplete} />
          <div className={classNames(Css.actions, compact && Css.compact)}>
            {(!outerButtons || commentType !== CommentTypes.COMMENT) && (
              <Button
                outline
                light={!outerButtons}
                title={uiTexts.send}
                className={classNames(Css.sendButton, !outerButtons && Css.iconOnly)}
                disabled={disabled || !comment.trim() || fetchingComments}
                onClick={handleSendMessageClick}>
                {outerButtons ? uiTexts.send : (
                  <>
                    <Icons.PaperPlaneTilt weight="bold" />
                    <Icons.PaperPlaneTilt weight="fill" />
                  </>
                )}
              </Button>
            )}
            {outerButtons && commentType === CommentTypes.COMMENT && (
              <Button
                primary
                disabled={!comment.trim() || fetchingComments}
                onClick={handleAskClient}>
                {uiTexts.sendAndAskClient}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(AddCommentField);
